import { apiDelete, apiGet, apiPost, apiPublicGet, apiPut } from './apiClient';

export enum ConsultingPricingRuleType {
  Percentage = 0,
  FixedTotal = 1,
  BuyXGetY = 2,
}

export interface ConsultingPricingConfigDto {
  id: number;
  serviceKey: string;
  baseSessionPrice: number;
  currency: string;
  basePriceText: string;
  pageIntroText?: string | null;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface ConsultingPricingRuleDto {
  id: number;
  serviceKey: string;
  isActive: boolean;
  priority: number;
  minSessions: number;
  ruleType: ConsultingPricingRuleType;
  percentOff?: number | null;
  fixedTotalPrice?: number | null;
  freeSessions?: number | null;
  displayText: string;
  snapshotLabel?: string | null;
  startAtUtc?: string | null;
  endAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface ConsultingPricingPublicDto {
  config: ConsultingPricingConfigDto;
  rules: ConsultingPricingRuleDto[];
}

export interface CreateConsultingPricingConfigDto {
  baseSessionPrice: number;
  currency: string;
  basePriceText: string;
  pageIntroText?: string | null;
  isActive: boolean;
}

export interface CreateConsultingPricingRuleDto {
  serviceKey?: string;
  isActive: boolean;
  priority: number;
  minSessions: number;
  ruleType: ConsultingPricingRuleType;
  percentOff?: number | null;
  fixedTotalPrice?: number | null;
  freeSessions?: number | null;
  displayText: string;
  snapshotLabel?: string | null;
  startAtUtc?: string | null;
  endAtUtc?: string | null;
}

export interface ConsultingPricingCalculationDto {
  sessionCount: number;
  baseSessionPrice: number;
  subtotal: number;
  discountAmount: number;
  totalAmount: number;
  appliedRule?: ConsultingPricingRuleDto | null;
}

export const calculateConsultingQuote = (
  baseSessionPrice: number,
  rules: ConsultingPricingRuleDto[],
  sessionCount: number
): ConsultingPricingCalculationDto => {
  const applicable = [...rules]
    .filter(rule => rule.isActive && rule.minSessions <= sessionCount)
    .sort((a, b) => b.priority - a.priority || b.minSessions - a.minSessions);

  const appliedRule = applicable[0] ?? null;
  const subtotal = baseSessionPrice * sessionCount;
  let totalAmount = subtotal;
  let discountAmount = 0;

  if (appliedRule) {
    switch (appliedRule.ruleType) {
      case ConsultingPricingRuleType.Percentage:
        discountAmount = subtotal * ((appliedRule.percentOff ?? 0) / 100);
        totalAmount = Math.max(subtotal - discountAmount, 0);
        break;
      case ConsultingPricingRuleType.FixedTotal:
        totalAmount = Math.min(Math.max(appliedRule.fixedTotalPrice ?? subtotal, 0), subtotal);
        discountAmount = Math.max(subtotal - totalAmount, 0);
        break;
      case ConsultingPricingRuleType.BuyXGetY: {
        const buySessions = Math.max(appliedRule.minSessions, 1);
        const freeSessions = Math.max(appliedRule.freeSessions ?? 0, 0);
        const groups = Math.floor(sessionCount / buySessions);
        const freeCount = groups * freeSessions;
        discountAmount = Math.min(freeCount * baseSessionPrice, subtotal);
        totalAmount = Math.max(subtotal - discountAmount, 0);
        break;
      }
    }
  }

  return {
    sessionCount,
    baseSessionPrice,
    subtotal,
    discountAmount,
    totalAmount,
    appliedRule,
  };
};

const ConsultingPricingService = {
  getPublic: async (): Promise<ConsultingPricingPublicDto | null> => {
    try {
      return await apiPublicGet<ConsultingPricingPublicDto>('/ConsultingPricing/public');
    } catch {
      return null;
    }
  },

  getAdmin: async (): Promise<ConsultingPricingPublicDto | null> => {
    try {
      return await apiGet<ConsultingPricingPublicDto>('/ConsultingPricing');
    } catch {
      return null;
    }
  },

  updateConfig: async (dto: CreateConsultingPricingConfigDto): Promise<ConsultingPricingConfigDto | null> => {
    try {
      return await apiPut<ConsultingPricingConfigDto>('/ConsultingPricing/config', dto);
    } catch {
      return null;
    }
  },

  createRule: async (dto: CreateConsultingPricingRuleDto): Promise<ConsultingPricingRuleDto | null> => {
    try {
      return await apiPost<ConsultingPricingRuleDto>('/ConsultingPricing/rules', dto);
    } catch {
      return null;
    }
  },

  updateRule: async (id: number, dto: CreateConsultingPricingRuleDto): Promise<ConsultingPricingRuleDto | null> => {
    try {
      return await apiPut<ConsultingPricingRuleDto>(`/ConsultingPricing/rules/${id}`, dto);
    } catch {
      return null;
    }
  },

  deleteRule: async (id: number): Promise<boolean> => {
    try {
      const result = await apiDelete<{ deleted: boolean }>(`/ConsultingPricing/rules/${id}`);
      return Boolean(result?.deleted);
    } catch {
      return false;
    }
  },

  calculate: async (sessionCount: number): Promise<ConsultingPricingCalculationDto | null> => {
    try {
      return await apiPublicGet<ConsultingPricingCalculationDto>(`/ConsultingPricing/calculate/${sessionCount}`);
    } catch {
      return null;
    }
  },
};

export default ConsultingPricingService;
